import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword({ admin = false }) {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post(admin ? "/admin/reset-password" : "/auth/reset-password", { token, password });
      toast.success("Password reset successful");
      nav(admin ? "/secure-admin/login" : "/login");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <h2 className="text-2xl font-bold tracking-tight text-center">Set a new password</h2>
        <p className="text-muted-foreground text-sm mt-1 mb-6 text-center">Choose a strong password for your account.</p>
        {!token && <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm px-3 py-2">Missing reset token. Please use the link from your email.</div>}
        {err && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">{err}</div>}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="password">New password</Label>
            <Input id="password" data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading || !token} className="w-full h-11" data-testid="reset-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reset Password
          </Button>
        </form>
        <p className="text-sm text-center text-muted-foreground mt-6">
          <Link to={admin ? "/secure-admin/login" : "/login"} className="font-semibold text-brand hover:underline">Back to login</Link>
        </p>
      </div>
    </div>
  );
}
